import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/app/store";
import { OLIVE_LOGO_SRC } from "@/constants";
import { fetchBranding } from "@/features/settings/model/brandingSlice";

/**
 * The tenant's branding as stored, for the Settings form. Refetches on mount even though
 * `MainLayout` loaded it at sign-in: the image URLs are presigned and expire, and a tab left open
 * long enough would otherwise open this form on links that no longer load.
 */
export function useBranding() {
  const dispatch = useAppDispatch();
  const { status, error, branding } = useAppSelector((state) => state.branding);

  useEffect(() => {
    void dispatch(fetchBranding());
  }, [dispatch]);

  return { status, error, branding };
}

/**
 * What the sidebar and browser tab show. Each part falls back on its own:
 * - `iconSrc` — the tenant's icon, else Olive's mark. Collapsed sidebar and browser tab.
 * - `fullLogoSrc` — icon and name in one image, shown alone when expanded; else `null`, meaning
 *   show the icon beside `brandName` as text.
 * - `brandName` — the tenant's name, else "Olive". Also the full logo's alt text.
 */
export function useBrandDisplay() {
  const { t } = useTranslation("common");
  const branding = useAppSelector((state) => state.branding.branding);

  return {
    brandName: branding.brand_name ?? t("sidebar.brandName"),
    iconSrc: branding.icon_url ?? OLIVE_LOGO_SRC,
    fullLogoSrc: branding.full_logo_url,
  };
}
